import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigationType } from "react-router-dom";
import { nextStack, pickBack, type BackTarget, type NavEntry, type PageParent } from "../utils/pageBack";

/**
 * The top bar's left side belongs to the page: a tab page puts its title
 * there, a detail page its way back ("‹ Orlando Parks Week"), a form its
 * Cancel. Pages say so with `usePageBar`; the bar works out the rest.
 */
export interface PageBarOptions {
  /** What this page is called, which is how the way back to it reads. */
  title?: string | null;
  /** Where "up" is. Leave out on a tab page, which has no way back. */
  parent?: PageParent | null;
  /** Always go up to `parent`, never back through history (levels within one page). */
  upOnly?: boolean;
  /** Tab pages: show the title in the bar on phones (the page's own h1 hides there). */
  showTitle?: boolean;
  /** Forms: the bar's back reads "Cancel" (or this label) and calls this. */
  cancel?: { label?: string; onClick: () => void } | null;
}

interface BarState {
  title: string | null;
  parent: PageParent | null;
  showTitle: boolean;
  upOnly: boolean;
  cancelLabel: string | null;
}

export type BarBack =
  | BackTarget
  | { kind: "cancel"; label: string; onClick: () => void };

interface PageBarView {
  back: BarBack | null;
  /** The tab page's title for the bar, when it asked for one. */
  title: string | null;
  setActionSlot: (el: HTMLElement | null) => void;
}

interface PageBarApi {
  set: (key: string, state: BarState) => void;
  clear: (key: string) => void;
  cancelRef: React.MutableRefObject<(() => void) | null>;
  actionSlot: HTMLElement | null;
}

const ViewContext = createContext<PageBarView | null>(null);
const ApiContext = createContext<PageBarApi | null>(null);

export function PageBarProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navType = useNavigationType();
  const [stack, setStack] = useState<NavEntry[]>([]);
  const [titles, setTitles] = useState<Record<string, string | undefined>>({});
  const [bar, setBar] = useState<{ key: string; state: BarState } | null>(null);
  const [actionSlot, setActionSlot] = useState<HTMLElement | null>(null);
  const cancelRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    setStack((s) => nextStack(s, { key: location.key, path: location.pathname }, navType));
  }, [location.key, location.pathname, navType]);

  const set = useCallback((key: string, state: BarState) => {
    setBar({ key, state });
    setTitles((t) => (t[key] === (state.title ?? undefined) ? t : { ...t, [key]: state.title ?? undefined }));
  }, []);
  const clear = useCallback((key: string) => {
    setBar((b) => (b?.key === key ? null : b));
  }, []);

  // Only the bar for the page on screen counts; a page leaving clears its own.
  const state = bar?.key === location.key ? bar.state : null;
  const view = useMemo<PageBarView>(() => {
    let back: BarBack | null = null;
    if (state?.cancelLabel != null) {
      back = { kind: "cancel", label: state.cancelLabel, onClick: () => cancelRef.current?.() };
    } else if (state) {
      back = state.upOnly
        ? state.parent && { kind: "parent", label: state.parent.label, to: state.parent.to }
        : pickBack(stack, titles, state.parent);
    }
    return { back, title: state?.showTitle ? state.title : null, setActionSlot };
  }, [state, stack, titles]);

  const api = useMemo<PageBarApi>(() => ({ set, clear, cancelRef, actionSlot }), [set, clear, actionSlot]);

  return (
    <ApiContext.Provider value={api}>
      <ViewContext.Provider value={view}>{children}</ViewContext.Provider>
    </ApiContext.Provider>
  );
}

/** For the bar itself. */
export function usePageBarView(): PageBarView {
  const v = useContext(ViewContext);
  if (!v) throw new Error("usePageBarView outside PageBarProvider");
  return v;
}

/** Tells the top bar this page's title and its way back. */
export function usePageBar({ title, parent, showTitle, upOnly, cancel }: PageBarOptions): void {
  const api = useContext(ApiContext);
  const { key } = useLocation();
  const set = api?.set;
  const clear = api?.clear;
  if (api) api.cancelRef.current = cancel?.onClick ?? null;
  const cancelLabel = cancel ? (cancel.label ?? "Cancel") : null;
  const parentTo = parent?.to ?? null;
  const parentLabel = parent?.label ?? null;

  useEffect(() => {
    if (!set) return;
    set(key, {
      title: title ?? null,
      parent: parentTo != null ? { to: parentTo, label: parentLabel ?? "Back" } : null,
      showTitle: !!showTitle,
      upOnly: !!upOnly,
      cancelLabel,
    });
  }, [set, key, title, parentTo, parentLabel, showTitle, upOnly, cancelLabel]);

  useEffect(() => {
    if (!clear) return;
    return () => clear(key);
  }, [clear, key]);
}

/** Puts a tab page's main button (+ New) in the top bar on phones. */
export function PageBarAction({ children }: { children: ReactNode }) {
  const slot = useContext(ApiContext)?.actionSlot;
  return slot ? createPortal(children, slot) : null;
}
